import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { ShoppingBag, Heart, User, Search, Menu, X, LogOut, LayoutDashboard } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useCart } from '../../context/CartContext';
import { useWishlist } from '../../context/WishlistContext';

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const { user, logout, isAdmin } = useAuth();
  const { itemCount } = useCart();
  const { wishlist } = useWishlist();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', handler);
    return () => window.removeEventListener('scroll', handler);
  }, []);

  useEffect(() => { setMenuOpen(false); setUserMenuOpen(false); }, [location]);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/collection?search=${encodeURIComponent(searchQuery)}`);
      setSearchOpen(false);
      setSearchQuery('');
    }
  };

  const handleLogout = () => { logout(); navigate('/'); };

  return (
    <>
      <nav style={{
        position: 'fixed', top: 0, left: 0, right: 0, zIndex: 1000,
        background: scrolled ? 'rgba(10,10,10,0.97)' : 'transparent',
        backdropFilter: scrolled ? 'blur(20px)' : 'none',
        borderBottom: scrolled ? '1px solid rgba(201,168,76,0.15)' : '1px solid transparent',
        transition: 'all 0.4s ease',
        padding: '0 32px',
        height: '72px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
      }}>
        {/* Logo */}
        <Link to="/" style={{ fontFamily: 'var(--font-display)', fontSize: '24px', fontWeight: 600, color: 'var(--gold)', letterSpacing: '0.1em' }}>
          LUMIÈRE
        </Link>

        {/* Desktop Nav Links */}
        <div style={{ display: 'flex', gap: '40px', alignItems: 'center' }} className="desktop-nav">
          {[['HOME', '/'], ['COLLECTION', '/collection'], ['AI ADVISOR', '/ai-advisor']].map(([label, path]) => (
            <Link key={path} to={path} style={{
              fontSize: '11px', letterSpacing: '0.2em', fontWeight: 500,
              color: location.pathname === path ? 'var(--gold)' : 'var(--white-dim)',
              transition: 'color 0.3s ease',
              position: 'relative',
            }}
              onMouseEnter={e => e.target.style.color = 'var(--gold)'}
              onMouseLeave={e => e.target.style.color = location.pathname === path ? 'var(--gold)' : 'var(--white-dim)'}
            >{label}</Link>
          ))}
        </div>

        {/* Icons */}
        <div style={{ display: 'flex', gap: '20px', alignItems: 'center' }}>
          <button onClick={() => setSearchOpen(!searchOpen)} style={{ background: 'none', color: 'var(--white-dim)', transition: 'color 0.3s' }}
            onMouseEnter={e => e.currentTarget.style.color = 'var(--gold)'}
            onMouseLeave={e => e.currentTarget.style.color = 'var(--white-dim)'}
          ><Search size={18} /></button>

          {user && (
            <Link to="/wishlist" style={{ position: 'relative', color: 'var(--white-dim)' }}>
              <Heart size={18} />
              {wishlist.length > 0 && (
                <span style={{ position: 'absolute', top: '-6px', right: '-6px', background: 'var(--gold)', color: 'var(--black)', width: '16px', height: '16px', borderRadius: '50%', fontSize: '9px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700 }}>
                  {wishlist.length}
                </span>
              )}
            </Link>
          )}

          <Link to="/cart" style={{ position: 'relative', color: 'var(--white-dim)' }}>
            <ShoppingBag size={18} />
            {itemCount > 0 && (
              <span style={{ position: 'absolute', top: '-6px', right: '-6px', background: 'var(--gold)', color: 'var(--black)', width: '16px', height: '16px', borderRadius: '50%', fontSize: '9px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700 }}>
                {itemCount}
              </span>
            )}
          </Link>

          {/* User Menu */}
          <div style={{ position: 'relative' }}>
            <button onClick={() => setUserMenuOpen(!userMenuOpen)} style={{ background: 'none', color: 'var(--white-dim)' }}>
              <User size={18} />
            </button>
            {userMenuOpen && (
              <div style={{ position: 'absolute', right: 0, top: '36px', background: 'var(--dark-2)', border: '1px solid var(--border)', minWidth: '180px', padding: '8px 0', zIndex: 100 }}>
                {user ? (
                  <>
                    <div style={{ padding: '10px 16px', borderBottom: '1px solid var(--border)', fontSize: '12px', color: 'var(--white-dim)' }}>
                      {user.name}
                    </div>
                    <Link to="/profile" style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 16px', fontSize: '12px', letterSpacing: '0.05em' }}
                      onMouseEnter={e => e.currentTarget.style.color = 'var(--gold)'}
                      onMouseLeave={e => e.currentTarget.style.color = 'var(--white)'}
                    ><User size={14} /> Profile</Link>
                    {isAdmin && (
                      <Link to="/admin" style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 16px', fontSize: '12px', letterSpacing: '0.05em' }}
                        onMouseEnter={e => e.currentTarget.style.color = 'var(--gold)'}
                        onMouseLeave={e => e.currentTarget.style.color = 'var(--white)'}
                      ><LayoutDashboard size={14} /> Admin</Link>
                    )}
                    <button onClick={handleLogout} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 16px', fontSize: '12px', width: '100%', background: 'none', color: 'var(--white)', letterSpacing: '0.05em' }}
                      onMouseEnter={e => e.currentTarget.style.color = '#ef4444'}
                      onMouseLeave={e => e.currentTarget.style.color = 'var(--white)'}
                    ><LogOut size={14} /> Sign Out</button>
                  </>
                ) : (
                  <>
                    <Link to="/login" style={{ display: 'block', padding: '10px 16px', fontSize: '12px' }}>Sign In</Link>
                    <Link to="/register" style={{ display: 'block', padding: '10px 16px', fontSize: '12px' }}>Register</Link>
                  </>
                )}
              </div>
            )}
          </div>

          {/* Mobile menu */}
          <button onClick={() => setMenuOpen(!menuOpen)} className="mobile-menu-btn" style={{ background: 'none', color: 'var(--white)', display: 'none' }}>
            {menuOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </nav>

      {/* Search overlay */}
      {searchOpen && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(10,10,10,0.95)', zIndex: 999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 32px' }}>
          <button onClick={() => setSearchOpen(false)} style={{ position: 'absolute', top: '24px', right: '32px', background: 'none', color: 'var(--white-dim)' }}><X size={24} /></button>
          <form onSubmit={handleSearch} style={{ width: '100%', maxWidth: '600px' }}>
            <input
              autoFocus
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              placeholder="Search fragrances, brands..."
              style={{ width: '100%', background: 'transparent', border: 'none', borderBottom: '1px solid var(--gold)', padding: '16px 0', fontSize: '28px', fontFamily: 'var(--font-display)', color: 'var(--white)', outline: 'none' }}
            />
            <p style={{ marginTop: '12px', fontSize: '11px', color: 'var(--white-dim)', letterSpacing: '0.1em' }}>PRESS ENTER TO SEARCH</p>
          </form>
        </div>
      )}

      {/* Mobile menu */}
      {menuOpen && (
        <div style={{ position: 'fixed', inset: 0, background: 'var(--black)', zIndex: 998, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '32px' }}>
          {[['HOME', '/'], ['COLLECTION', '/collection'], ['AI ADVISOR', '/ai-advisor'], ['CART', '/cart'], ['PROFILE', '/profile']].map(([label, path]) => (
            <Link key={path} to={path} style={{ fontSize: '24px', fontFamily: 'var(--font-display)', letterSpacing: '0.2em', color: 'var(--white)' }}>{label}</Link>
          ))}
        </div>
      )}

      <style>{`
        @media (max-width: 768px) {
          .desktop-nav { display: none !important; }
          .mobile-menu-btn { display: block !important; }
        }
      `}</style>
    </>
  );
};

export default Navbar;
